import styled from 'styled-components'
import { Text } from '../../../../../components/Ui-Kits/Text'
import { InputText } from '../../../../../components/Ui-Kits/InputText'
import BaseButton from '../../../../../components/Button/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faPenClip, faClock } from '@fortawesome/pro-light-svg-icons'
import { useAuctionProduct } from 'hooks/data/auction/useAuctionProduct'
import { useParams } from 'react-router'
import { createBid } from 'cloudFunctions'
import { useCallback, useMemo, useState } from 'react'
import { useMyLastPreBid } from 'hooks/data/auction/useMyWinningPreBid'
import { Col, Row } from 'components/Ui-Kits/Grid'
import { useAuthState } from 'react-firebase-hooks/auth'
import { auth } from 'data/firebase'
import { useAppTranslation } from 'i18n/hooks'
import { getMinimumRise } from 'helpers/getMinimumRise'
import { useCurrentUserProfile } from 'hooks/data/useCurrentUserProfile'
import { useAlert } from 'hooks/useAlert'
import { Loading, LoadingWrap } from 'components/Shared/Loading'
import { HighestBidBox } from './HighestBidBox'
import { contactUrls } from 'constants/index'
import qs from 'querystring'
import { any, bool, func, number } from 'prop-types' // Updated to include `func`
import dayjs from 'dayjs'
import { Modal } from 'components/Shared/Modal'
import useBidValidation from './hooks/useBidValidation'

function BidModal({ show, onClose, bidAmount, onConfirm }) {
  const { t } = useAppTranslation()
  const description = t('confirmBidDescription', { bidAmount, percentage: 22 }).split('\n')
  return (
    <Modal show={show} title={t('confirmBid')} onClose={onClose}>
      <Wrap>
        <p>
          {description.map((item, i) => (
            <p key={`item${i}`}>{item}</p>
          ))}
        </p>
        <br />
        <p>{t('doYouWantToProceed')}</p>
        <Row mt={0.5} mb={0.5}>
          <Col col={6} sm={12}>
            <Button color="primary" onClick={onConfirm}>
              {t('bidButtonLabel', { minimumBid: bidAmount })}
            </Button>
          </Col>
          <Col col={6} sm={12}>
            <Button color="primary" secondary={true} onClick={onClose}>
              {t('cancel')}
            </Button>
          </Col>
        </Row>
      </Wrap>
    </Modal>
  )
}

BidModal.propTypes = {
  show: bool.isRequired,
  onClose: func.isRequired,
  bidAmount: number.isRequired,
  onConfirm: func.isRequired,
}

export const BidBox = ({ auction }) => {
  const { auctionId, productId: itemId } = useParams()
  const { product } = useAuctionProduct(auctionId, itemId)
  const [currentBid, setCurrentBid] = useState()
  const { bid } = useMyLastPreBid(auctionId, itemId)
  const [user] = useAuthState(auth)
  const { profile } = useCurrentUserProfile()
  const [bidError, setBidError] = useState(undefined)
  const [showModal, setShowModal] = useState(false)
  const [pendingBid, setPendingBid] = useState(null)
  const {
    t,
    i18n: { language },
  } = useAppTranslation()
  const [isBidding, setIsBidding] = useState(false)
  const alert = useAlert()

  const handleOnClose = () => {
    setShowModal(false)
    setPendingBid(null)
  }

  const handleConfirmBid = async () => {
    if (pendingBid === null) return
    setShowModal(false)
    setIsBidding(true)
    try {
      await createBid({ amount: pendingBid, auctionId, itemId })
      alert(t('bidPlacedSuccessfully'), 'success')
      setCurrentBid('')
    } catch (error) {
      alert(t('bidCreationFailed'), 'danger')
    } finally {
      setIsBidding(false)
      setPendingBid(null)
    }
  }

  const { minimumBid, minimumRise } = useMemo(() => {
    if (!product) return {}

    const secondHighestBid = product?.secondHighestBid || product?.technicalDetails?.AuctionMinimumPrice
    const minimumRise = (product?.secondHighestBid && getMinimumRise(product?.secondHighestBid)) || 0
    const minimumBid = product?.secondHighestBid
      ? secondHighestBid + getMinimumRise(secondHighestBid)
      : secondHighestBid

    return { secondHighestBid, minimumBid, minimumRise }
  }, [product])

  const prebidEndDate = useMemo(() => {
    return {
      date: dayjs(auction?.endDatePreBid).format('DD.MM.YYYY'),
      time: dayjs(auction?.endDatePreBid).format('HH:mm'),
    }
  }, [auction?.endDatePreBid])

  const startDate = useMemo(() => {
    return {
      date: dayjs(auction?.startDate).format('DD.MM.YYYY'),
      time: dayjs(auction?.startDate).format('HH:mm'),
    }
  }, [auction?.startDate])

  const validateBid = useBidValidation({
    isAuthenticated: user !== null,
    isVerified: profile?.verified,
    minimumBid,
    maxBiddingLevel: profile?.maxBiddingLevel,
    t,
    alert,
  })

  const bidAmountHandler = useCallback(
    (amount = false) => {
      setBidError(undefined)

      const isValid = validateBid(amount)
      if (!isValid) return

      setPendingBid(amount)
      setShowModal(true)
    },
    [validateBid]
  )

  const askMoreURL = useMemo(() => {
    const url = contactUrls[language]
    const query = {
      sku: product?.sku,
    }
    if (profile) {
      Object.assign(query, {
        firstname: profile.firstName,
        lastname: profile.lastName,
        email: profile.email,
        phone: profile.phone,
        subject: 'Auctions',
      })
    }

    return `${url}?${qs.stringify(query)}#contact`
  }, [profile, language, product?.sku])

  return (
    <Wrap>
      <HighestBidBox
        bid={bid}
        hasBids={!!product?.secondHighestBid}
        amount={product?.secondHighestBid || product?.technicalDetails?.AuctionMinimumPrice}
      />
      <TextWrap>
        <Icon icon={faInfoCircle} />
        <IconTextWrap color="black">{t('minimumBidForItemIs', { minimumBid: minimumRise })}</IconTextWrap>
      </TextWrap>
      <Button color="primary" onClick={() => bidAmountHandler(minimumBid)} disabled={isBidding || bid?.isWinning}>
        {t('bidButtonLabel', { minimumBid })}
      </Button>
      <TextWrap>
        <Text color="black" bold>
          {t('or')}
        </Text>
      </TextWrap>
      <InputText
        noBorder
        placeholder={t('bitTextInputPlaceholder', { minimumBid })}
        value={currentBid}
        onChange={e => {
          const value = e.target.value
          if (bidError && !value) {
            setBidError(undefined)
          }

          const numericValue = parseFloat(value)
          if (value && (isNaN(numericValue) || numericValue < minimumBid)) {
            setBidError(t('bidShouldBeMoreThanAmount', { minimumBid }))
          } else {
            setBidError(undefined)
          }

          setCurrentBid(value)
        }}
        disabled={isBidding}
        errorHint={bidError}
      />
      <Button color="primary" disabled={isBidding} onClick={() => bidAmountHandler(parseFloat(currentBid), true)}>
        {t('bid')}
      </Button>
      <TextWrap>
        <Icon icon={faPenClip} />
        <IconTextWrap color="black">{t('preBidUntil', prebidEndDate)}</IconTextWrap>
      </TextWrap>
      <TextWrap>
        <Icon icon={faClock} />
        <IconTextWrap color="black">{t('auctionDate', startDate)}</IconTextWrap>
      </TextWrap>
      <Button
        color="primary"
        secondary
        onClick={() => {
          window.location.href = askMoreURL
        }}
        target="_blank"
      >
        {t('askMoreAboutTheProduct')}
      </Button>
      {isBidding && (
        <LoadingWrap>
          <Loading />
        </LoadingWrap>
      )}
      <BidModal show={showModal} onClose={handleOnClose} bidAmount={pendingBid} onConfirm={handleConfirmBid} />
    </Wrap>
  )
}

BidBox.propTypes = {
  auction: any,
}

const Wrap = styled.div`
  padding: 1rem;
  background-color: var(--color-grey);
  margin-bottom: 1.5rem;
  position: relative;
`
const TextWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${({ justify = 'space-between' }) => justify};
  align-items: ${({ align = 'flex-start' }) => align};
  margin-bottom: ${({ noMargin }) => !noMargin && '1rem'};
`
const IconTextWrap = styled(Text)`
  flex: 1;
`
const Icon = styled(FontAwesomeIcon)`
  color: var(--color-black);
  font-size: 0.9rem;
  margin-right: 0.375rem;
`
const Button = styled(BaseButton)`
  width: 100%;
  display: block;
  margin-bottom: 1rem;
`
